import {FC, useState, useMemo} from 'react'
import {ReservationService} from '../../../../../Types/HotelBookingTypes'
import {i18n} from '../../../../translate/i18n'

interface ServicesListTabProps {
  services: ReservationService[]
}

const ServicesListTab: FC<ServicesListTabProps> = ({services}) => {
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'badge-success'
      case 'pending':
        return 'badge-warning'
      case 'cancelled':
        return 'badge-danger'
      case 'completed':
        return 'badge-info'
      default:
        return 'badge-secondary'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return i18n.t('hospitality_room_reservation_tabs.services_list.status_confirmed')
      case 'pending':
        return i18n.t('hospitality_room_reservation_tabs.services_list.status_pending')
      case 'cancelled':
        return i18n.t('hospitality_room_reservation_tabs.services_list.status_cancelled')
      case 'completed':
        return i18n.t('hospitality_room_reservation_tabs.services_list.status_completed')
      default:
        return status
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  // const formatDateTime = (dateString: string) => {
  //   return new Date(dateString).toLocaleString('pt-BR', {
  //     day: '2-digit',
  //     month: '2-digit',
  //     year: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit'
  //   })
  // }

  // Filtrar serviços
  const filteredServices = useMemo(() => {
    let filtered = services

    // Filtro por status
    if (filterStatus !== 'all') {
      filtered = filtered.filter((service) => service.status.toLowerCase() === filterStatus)
    }

    // Filtro por busca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((service) => {
        const serviceName = service.post?.title?.toLowerCase() || ''
        const serviceDescription = service.description?.toLowerCase() || ''
        const roomNumber = service.content?.room?.roomNumber?.toLowerCase() || ''
        const bookingTitle = service.gridHotelBooking?.title?.toLowerCase() || ''

        return (
          serviceName.includes(query) ||
          serviceDescription.includes(query) ||
          roomNumber.includes(query) ||
          bookingTitle.includes(query)
        )
      })
    }

    return filtered
  }, [services, filterStatus, searchQuery])

  // Calcular totais
  const getTotalAmount = () => {
    return filteredServices.reduce((total, service) => total + (service.price || 0), 0)
  }

  const getTotalPeople = () => {
    return filteredServices.reduce((total, service) => total + (service.peopleQty || 0), 0)
  }

  // Status únicos para o filtro
  const uniqueStatuses = useMemo(() => {
    const statuses = services.map((s) => s.status.toLowerCase())
    return Array.from(new Set(statuses))
  }, [services])

  // Paginação
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage)
  const paginatedServices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filteredServices.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredServices, currentPage, itemsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleViewDetails = (service: ReservationService) => {
    // Implementar modal ou drawer de detalhes
    console.log('View details:', service)
  }

  // Configuração das colunas da tabela
  const renderTableRow = (service: ReservationService) => {
    return (
      <tr key={service.id} className='align-middle'>
        <td className='text-center'>
          <span className='fw-bold text-primary'>#{service.id}</span>
        </td>
        <td>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-40px me-3'>
              <img
                src={service.post?.img || '/media/svg/files/blank-image.svg'}
                alt={service.post?.title}
                className='rounded'
                onError={(e) => {
                  e.currentTarget.src = '/media/svg/files/blank-image.svg'
                }}
              />
            </div>
            <div>
              <div className='fw-bold'>{service.post?.title || 'N/A'}</div>
              <small className='text-muted'>{service.gridHotelBooking?.title || ''}</small>
            </div>
          </div>
        </td>
        <td>
          <div className='fw-bold'>{service.content?.room?.roomNumber || 'N/A'}</div>
          <small className='text-muted text-capitalize'>{service.content?.room?.type || ''}</small>
        </td>
        <td>
          <div className='fw-bold'>{formatDate(service.reservationDate)}</div>
          {service.reservationTime && (
            <small className='text-muted'>{service.reservationTime}</small>
          )}
        </td>
        <td className='text-center'>
          <i className='fas fa-users text-info me-1'></i>
          <span className='fw-bold'>{service.peopleQty}</span>
        </td>
        <td className='text-end'>
          <span className='fw-bold text-success'>R$ {service.price.toFixed(2)}</span>
        </td>
        <td className='text-center'>
          <span className={`badge ${getStatusBadgeClass(service.status)} fs-7`}>
            {getStatusLabel(service.status)}
          </span>
        </td>
        <td className='text-center'>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light-primary'
            title={i18n.t('hospitality_room_reservation_tabs.services_list.btn_view_details')}
            onClick={() => handleViewDetails(service)}
          >
            <i className='fas fa-eye'></i>
          </button>
        </td>
      </tr>
    )
  }

  if (services.length === 0) {
    return (
      <div className='card'>
        <div className='card-body text-center py-10'>
          <i className='fas fa-concierge-bell text-muted fs-3x mb-4'></i>
          <h3 className='text-muted'>
            {i18n.t('hospitality_room_reservation_tabs.services_list.no_services')}
          </h3>
          <p className='text-muted'>
            {i18n.t('hospitality_room_reservation_tabs.services_list.no_services_description')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Cards de Resumo */}
      <div className='row mb-6'>
        <div className='col-md-3'>
          <div className='card card-custom bg-light-primary'>
            <div className='card-body text-center p-4'>
              <i className='fas fa-list text-primary fs-2x mb-3'></i>
              <h3 className='text-primary fw-bold'>{filteredServices.length}</h3>
              <p className='text-muted mb-0'>
                {i18n.t('hospitality_room_reservation_tabs.services_list.total_filtered')}
              </p>
              {services.length !== filteredServices.length && (
                <small className='text-muted'>
                  {i18n.t('hospitality_room_reservation_tabs.services_list.of_total', {
                    total: services.length,
                  })}
                </small>
              )}
            </div>
          </div>
        </div>
        <div className='col-md-3'>
          <div className='card card-custom bg-light-success'>
            <div className='card-body text-center p-4'>
              <i className='fas fa-money-bill-wave text-success fs-2x mb-3'></i>
              <h3 className='text-success fw-bold'>R$ {getTotalAmount().toFixed(2)}</h3>
              <p className='text-muted mb-0'>
                {i18n.t('hospitality_room_reservation_tabs.services_list.total_amount')}
              </p>
            </div>
          </div>
        </div>
        <div className='col-md-3'>
          <div className='card card-custom bg-light-info'>
            <div className='card-body text-center p-4'>
              <i className='fas fa-users text-info fs-2x mb-3'></i>
              <h3 className='text-info fw-bold'>{getTotalPeople()}</h3>
              <p className='text-muted mb-0'>
                {i18n.t('hospitality_room_reservation_tabs.services_list.total_people')}
              </p>
            </div>
          </div>
        </div>
        <div className='col-md-3'>
          <div className='card card-custom bg-light-warning'>
            <div className='card-body text-center p-4'>
              <i className='fas fa-calculator text-warning fs-2x mb-3'></i>
              <h3 className='text-warning fw-bold'>
                R${' '}
                {filteredServices.length > 0
                  ? (getTotalAmount() / filteredServices.length).toFixed(2)
                  : '0.00'}
              </h3>
              <p className='text-muted mb-0'>
                {i18n.t('hospitality_room_reservation_tabs.services_list.average_price')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className='card mb-6'>
        <div className='card-body'>
          <div className='row g-4'>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>
                {i18n.t('hospitality_room_reservation_tabs.services_list.filter_search')}
              </label>
              <div className='input-group'>
                <span className='input-group-text'>
                  <i className='fas fa-search'></i>
                </span>
                <input
                  type='text'
                  className='form-control'
                  placeholder={i18n.t(
                    'hospitality_room_reservation_tabs.services_list.filter_search_placeholder'
                  )}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type='button'
                    className='btn btn-light-danger'
                    onClick={() => setSearchQuery('')}
                  >
                    <i className='fas fa-times'></i>
                  </button>
                )}
              </div>
            </div>
            <div className='col-md-6'>
              <label className='form-label fw-bold'>
                {i18n.t('hospitality_room_reservation_tabs.services_list.filter_status')}
              </label>
              <select
                className='form-select'
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value='all'>
                  {i18n.t('hospitality_room_reservation_tabs.services_list.filter_status_all')}
                </option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filtros ativos */}
          {(filterStatus !== 'all' || searchQuery) && (
            <div className='mt-4'>
              <div className='d-flex align-items-center gap-2 flex-wrap'>
                <span className='text-muted fw-bold'>
                  {i18n.t('hospitality_room_reservation_tabs.services_list.active_filters')}:
                </span>
                {filterStatus !== 'all' && (
                  <span className='badge badge-light-primary'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.filter_status')}:{' '}
                    {getStatusLabel(filterStatus)}
                    <i
                      className='fas fa-times ms-2 cursor-pointer'
                      onClick={() => setFilterStatus('all')}
                    ></i>
                  </span>
                )}
                {searchQuery && (
                  <span className='badge badge-light-primary'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.filter_search')}: "
                    {searchQuery}"
                    <i
                      className='fas fa-times ms-2 cursor-pointer'
                      onClick={() => setSearchQuery('')}
                    ></i>
                  </span>
                )}
                <button
                  type='button'
                  className='btn btn-sm btn-light-danger'
                  onClick={() => {
                    setFilterStatus('all')
                    setSearchQuery('')
                  }}
                >
                  {i18n.t('hospitality_room_reservation_tabs.services_list.clear_filters')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabela de Serviços */}
      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>
            <i className='fas fa-table me-2'></i>
            {i18n.t('hospitality_room_reservation_tabs.services_list.table_title')}
          </h3>
          <div className='card-toolbar'>
            <span className='badge badge-light-primary fs-7'>
              {filteredServices.length}{' '}
              {i18n.t('hospitality_room_reservation_tabs.services_list.services')}
            </span>
          </div>
        </div>
        <div className='card-body p-0'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 mb-0'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='text-center min-w-50px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_id')}
                  </th>
                  <th className='min-w-200px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_service')}
                  </th>
                  <th className='min-w-120px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_room')}
                  </th>
                  <th className='min-w-130px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_date')}
                  </th>
                  <th className='text-center min-w-100px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_people')}
                  </th>
                  <th className='text-end min-w-120px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_price')}
                  </th>
                  <th className='text-center min-w-130px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_status')}
                  </th>
                  <th className='text-center min-w-100px'>
                    {i18n.t('hospitality_room_reservation_tabs.services_list.column_actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedServices.length > 0 ? (
                  paginatedServices.map(renderTableRow)
                ) : (
                  <tr>
                    <td colSpan={8} className='text-center py-10'>
                      <i className='fas fa-inbox text-muted fs-3x mb-4 d-block'></i>
                      <h4 className='text-muted'>
                        {i18n.t('hospitality_room_reservation_tabs.services_list.no_results')}
                      </h4>
                      <p className='text-muted'>
                        {i18n.t(
                          'hospitality_room_reservation_tabs.services_list.no_results_description'
                        )}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className='card-footer d-flex justify-content-between align-items-center'>
              <div className='d-flex align-items-center gap-2'>
                <select
                  className='form-select form-select-sm w-auto'
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className='text-muted fs-7'>
                  {i18n.t('hospitality_room_reservation_tabs.services_list.showing')}{' '}
                  {Math.min((currentPage - 1) * itemsPerPage + 1, filteredServices.length)}{' '}
                  {i18n.t('hospitality_room_reservation_tabs.services_list.to')}{' '}
                  {Math.min(currentPage * itemsPerPage, filteredServices.length)}{' '}
                  {i18n.t('hospitality_room_reservation_tabs.services_list.of')}{' '}
                  {filteredServices.length}
                </span>
              </div>

              <ul className='pagination mb-0'>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className='page-link'
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className='fas fa-chevron-left'></i>
                  </button>
                </li>

                {Array.from({length: totalPages}, (_, i) => i + 1).map((page) => {
                  // Mostrar apenas páginas próximas
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <li
                        key={page}
                        className={`page-item ${currentPage === page ? 'active' : ''}`}
                      >
                        <button className='page-link' onClick={() => handlePageChange(page)}>
                          {page}
                        </button>
                      </li>
                    )
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return (
                      <li key={page} className='page-item disabled'>
                        <span className='page-link'>...</span>
                      </li>
                    )
                  }
                  return null
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className='page-link'
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className='fas fa-chevron-right'></i>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ServicesListTab
